import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Delete } from "lucide-react";

/**
 * Calculator — Phase 5 · tiny utility.
 *
 * Standard 4-function + percent + sign toggle + memory-less. Uses a
 * lightweight expression evaluator (whitelist-safe: only digits, dot,
 * and + − × ÷ operators). No `eval`. Works offline.
 */
const BUTTONS = [
  ["AC", "±", "%",  "÷"],
  ["7",  "8", "9",  "×"],
  ["4",  "5", "6",  "−"],
  ["1",  "2", "3",  "+"],
  ["0",  ".",       "="],
];

function compute(expr) {
  // Sanitise — only digits, operators, dot, and unary minus after
  // an operator. Reject everything else.
  const clean = expr.replace(/×/g, "*").replace(/÷/g, "/").replace(/−/g, "-");
  if (!/^[-+*/.\d\s]+$/.test(clean)) return NaN;
  const tokens = clean.match(/(-?\d+\.?\d*|\+|-|\*|\/)/g);
  if (!tokens) return NaN;
  // Two-pass shunting yard: * / first, then + -.
  let pass1 = [tokens[0]];
  for (let i = 1; i < tokens.length; i += 2) {
    const op = tokens[i], b = parseFloat(tokens[i + 1]);
    if (op === "*" || op === "/") {
      const a = parseFloat(pass1.pop());
      pass1.push(op === "*" ? a * b : a / b);
    } else {
      pass1.push(op); pass1.push(b);
    }
  }
  let acc = parseFloat(pass1[0]);
  for (let i = 1; i < pass1.length; i += 2) {
    const op = pass1[i], b = parseFloat(pass1[i + 1]);
    acc = op === "+" ? acc + b : acc - b;
  }
  return acc;
}

export default function Calculator() {
  const [expr, setExpr] = useState("0");

  const press = (key) => {
    if (key === "AC")   { setExpr("0"); return; }
    if (key === "±")    { setExpr(String(parseFloat(expr) * -1 || 0)); return; }
    if (key === "%")    { setExpr(String((parseFloat(expr) || 0) / 100)); return; }
    if (key === "=")    { const r = compute(expr); setExpr(isFinite(r) ? String(Number(r.toFixed(10))) : "Error"); return; }
    // digit / operator / dot append
    if (expr === "0" && /^\d$/.test(key)) { setExpr(key); return; }
    if (expr === "Error") { setExpr(/^\d$/.test(key) ? key : "0"); return; }
    setExpr(expr + key);
  };

  const backspace = () => {
    if (expr.length <= 1) { setExpr("0"); return; }
    setExpr(expr.slice(0, -1));
  };

  return (
    <div className="max-w-md mx-auto py-6 px-4" data-testid="calculator-page">
      <h1 className="text-2xl font-black text-stone-900 mb-1">Calculator</h1>
      <p className="text-xs text-stone-500 mb-4">Offline · works without login</p>
      <Card className="rounded-3xl shadow-lg">
        <CardContent className="p-4">
          <div className="rounded-2xl bg-stone-900 text-white p-4 flex items-end justify-end min-h-[80px] text-4xl font-mono break-all" data-testid="calc-display">
            {expr}
          </div>
          <div className="grid grid-cols-4 gap-2 mt-3">
            {BUTTONS.flat().map((k, i) => (
              <Button key={i} variant={/[0-9.]/.test(k) ? "outline" : (k === "=" ? "default" : "secondary")}
                      onClick={() => press(k)}
                      className={`h-14 text-lg font-black ${k === "=" ? "col-span-2 bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                      data-testid={`calc-btn-${k}`}>
                {k}
              </Button>
            ))}
          </div>
          <div className="mt-2 flex justify-end">
            <Button variant="ghost" onClick={backspace} className="text-xs text-stone-500 gap-1" data-testid="calc-backspace">
              <Delete size={12}/> Backspace
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
